import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createWhiteboardProvider } from './whiteboard-provider'
import type { WhiteboardProviderResult } from './whiteboard-provider'
import { createLogger } from '../lib/logger'

const slog = createLogger('visio-whiteboard')

interface WhiteboardProps {
  onClose: () => void
  roomId: string
  embedded?: boolean
  externalProvider?: WhiteboardProviderResult | null
}

type Tool = 'pencil' | 'eraser' | 'rectangle' | 'circle'

interface Point {
  x: number
  y: number
}

interface StrokeData {
  tool: Tool
  points: Point[]
  color: string
  width: number
}

const COLORS = ['#000000', '#ef4444', '#3b82f6', '#22c55e', '#eab308', '#ffffff']
const WIDTHS = [
  { label: 'S', value: 2 },
  { label: 'M', value: 4 },
  { label: 'L', value: 8 },
]

// Fixed virtual canvas size — same for all participants
const VIRTUAL_W = 2000
const VIRTUAL_H = 1500
const MIN_ZOOM = 0.1
const MAX_ZOOM = 5
const ZOOM_STEP = 1.15

export function Whiteboard({ onClose, roomId, embedded, externalProvider }: WhiteboardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [tool, setTool] = useState<Tool>('pencil')
  const [color, setColor] = useState('#000000')
  const [strokeWidth, setStrokeWidth] = useState(2)

  // Viewport: zoom + pan (in virtual coords)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })

  const isDrawing = useRef(false)
  const isPanning = useRef(false)
  const panStart = useRef({ x: 0, y: 0 })
  const panStartOffset = useRef({ x: 0, y: 0 })
  const currentPoints = useRef<Point[]>([])
  const shapeStart = useRef<Point | null>(null)
  const snapshotRef = useRef<ImageData | null>(null)
  const isRemoteUpdate = useRef(false)
  const spaceDown = useRef(false)

  // Yjs CRDT provider — prefer external (persisted across open/close) over local
  const localProvider = useMemo(() => externalProvider ? null : createWhiteboardProvider(roomId), [roomId, !externalProvider])
  const { doc, provider } = externalProvider || localProvider!
  const providerRef = useRef(provider)
  providerRef.current = provider

  // Only cleanup locally-created provider on unmount
  useEffect(() => {
    if (!localProvider) return
    return () => {
      localProvider.provider.disconnect()
      localProvider.provider.destroy()
      localProvider.doc.destroy()
    }
  }, [localProvider])

  // Fit zoom so entire virtual canvas is visible
  const fitToView = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const rect = container.getBoundingClientRect()
    const scaleX = rect.width / VIRTUAL_W
    const scaleY = rect.height / VIRTUAL_H
    const newZoom = Math.min(scaleX, scaleY) * 0.95
    setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom)))
    // Center the canvas
    setPan({
      x: (rect.width - VIRTUAL_W * newZoom) / 2,
      y: (rect.height - VIRTUAL_H * newZoom) / 2,
    })
  }, [])

  // Resize canvas to fill container (pixel buffer)
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return
    const rect = container.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    canvas.style.width = `${rect.width}px`
    canvas.style.height = `${rect.height}px`
  }, [])

  // Draw everything: white background + all strokes with viewport transform
  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const dpr = window.devicePixelRatio || 1
    const w = canvas.width / dpr
    const h = canvas.height / dpr

    // Clear to dark bg (panel background)
    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.scale(dpr, dpr)
    ctx.fillStyle = '#18181b'
    ctx.fillRect(0, 0, w, h)

    // Apply viewport transform: pan + zoom
    ctx.translate(pan.x, pan.y)
    ctx.scale(zoom, zoom)

    // White virtual canvas
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, VIRTUAL_W, VIRTUAL_H)

    // Draw border around virtual canvas
    ctx.strokeStyle = '#71717a'
    ctx.lineWidth = 1 / zoom
    ctx.strokeRect(0, 0, VIRTUAL_W, VIRTUAL_H)

    // Draw all strokes
    const yStrokes = doc.getArray<StrokeData>('strokes')
    for (const s of yStrokes.toArray()) {
      drawStrokeOnCtx(ctx, s, zoom)
    }

    ctx.restore()
  }, [doc, zoom, pan])

  // Initial setup + resize
  useEffect(() => {
    resizeCanvas()
    fitToView()
    const onResize = () => {
      resizeCanvas()
      // Don't auto-fit on every resize, just update canvas buffer
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [resizeCanvas, fitToView])

  // Redraw whenever zoom/pan/strokes change
  useEffect(() => {
    redraw()
  }, [redraw])

  // Observe Yjs changes (remote strokes)
  useEffect(() => {
    const yStrokes = doc.getArray<StrokeData>('strokes')

    const observer = () => {
      if (isRemoteUpdate.current) return
      isRemoteUpdate.current = true
      redraw()
      isRemoteUpdate.current = false
    }

    yStrokes.observe(observer)
    return () => yStrokes.unobserve(observer)
  }, [doc, redraw])

  // Space key for pan mode
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault()
        spaceDown.current = true
      }
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spaceDown.current = false
      }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [])

  // Convert screen coords to virtual canvas coords
  const screenToVirtual = useCallback((clientX: number, clientY: number): Point => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    const sx = clientX - rect.left
    const sy = clientY - rect.top
    return {
      x: (sx - pan.x) / zoom,
      y: (sy - pan.y) / zoom,
    }
  }, [zoom, pan])

  const getPos = (e: React.MouseEvent | React.TouchEvent): Point => {
    let clientX: number, clientY: number
    if ('touches' in e) {
      const touch = e.touches[0] || (e as React.TouchEvent).changedTouches[0]
      clientX = touch.clientX
      clientY = touch.clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }
    return screenToVirtual(clientX, clientY)
  }

  const getScreenPos = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    if ('touches' in e) {
      const touch = e.touches[0] || (e as React.TouchEvent).changedTouches[0]
      return { x: touch.clientX, y: touch.clientY }
    }
    return { x: e.clientX, y: e.clientY }
  }

  const saveSnapshot = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (ctx) snapshotRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height)
  }

  const restoreSnapshot = () => {
    const canvas = canvasRef.current
    if (!canvas || !snapshotRef.current) return
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.putImageData(snapshotRef.current, 0, 0)
  }

  // --- Drawing handlers ---

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()

    // Pan mode: space+click or middle click or right click
    const isMiddle = 'button' in e && (e as React.MouseEvent).button === 1
    const isRight = 'button' in e && (e as React.MouseEvent).button === 2
    if (spaceDown.current || isMiddle || isRight) {
      isPanning.current = true
      const sp = getScreenPos(e)
      panStart.current = { x: sp.x, y: sp.y }
      panStartOffset.current = { x: pan.x, y: pan.y }
      return
    }

    isDrawing.current = true
    const pos = getPos(e)

    // Clamp to virtual canvas
    pos.x = Math.max(0, Math.min(VIRTUAL_W, pos.x))
    pos.y = Math.max(0, Math.min(VIRTUAL_H, pos.y))

    currentPoints.current = [pos]
    if (tool === 'rectangle' || tool === 'circle') {
      shapeStart.current = pos
      saveSnapshot()
    }
  }

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    // Pan
    if (isPanning.current) {
      e.preventDefault()
      const sp = getScreenPos(e)
      setPan({
        x: panStartOffset.current.x + (sp.x - panStart.current.x),
        y: panStartOffset.current.y + (sp.y - panStart.current.y),
      })
      return
    }

    if (!isDrawing.current) return
    e.preventDefault()
    const pos = getPos(e)

    // Clamp to virtual canvas
    pos.x = Math.max(0, Math.min(VIRTUAL_W, pos.x))
    pos.y = Math.max(0, Math.min(VIRTUAL_H, pos.y))

    if (tool === 'rectangle' || tool === 'circle') {
      // Live preview: restore snapshot then draw shape preview
      restoreSnapshot()
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const dpr = window.devicePixelRatio || 1
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
      ctx.translate(pan.x, pan.y)
      ctx.scale(zoom, zoom)
      ctx.strokeStyle = color
      ctx.lineWidth = strokeWidth
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      const start = shapeStart.current!
      if (tool === 'rectangle') {
        ctx.beginPath()
        ctx.rect(start.x, start.y, pos.x - start.x, pos.y - start.y)
        ctx.stroke()
      } else {
        const cx = (start.x + pos.x) / 2, cy = (start.y + pos.y) / 2
        const rx = Math.abs(pos.x - start.x) / 2, ry = Math.abs(pos.y - start.y) / 2
        ctx.beginPath()
        ctx.ellipse(cx, cy, Math.max(rx, 0.1), Math.max(ry, 0.1), 0, 0, Math.PI * 2)
        ctx.stroke()
      }
      ctx.restore()
      currentPoints.current = [shapeStart.current!, pos]
    } else {
      currentPoints.current.push(pos)
      // Incremental draw for pencil/eraser: just draw the last segment
      const pts = currentPoints.current
      const len = pts.length
      if (len >= 2) {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        const dpr = window.devicePixelRatio || 1
        ctx.save()
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.scale(dpr, dpr)
        ctx.translate(pan.x, pan.y)
        ctx.scale(zoom, zoom)
        if (tool === 'eraser') {
          ctx.globalCompositeOperation = 'destination-out'
        } else {
          ctx.globalCompositeOperation = 'source-over'
        }
        ctx.strokeStyle = color
        ctx.lineWidth = strokeWidth
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.beginPath()
        if (len === 2) {
          ctx.moveTo(pts[0].x, pts[0].y)
          ctx.lineTo(pts[1].x, pts[1].y)
        } else {
          const prev = pts[len - 3], curr = pts[len - 2], next = pts[len - 1]
          const midX1 = (prev.x + curr.x) / 2, midY1 = (prev.y + curr.y) / 2
          const midX2 = (curr.x + next.x) / 2, midY2 = (curr.y + next.y) / 2
          ctx.moveTo(midX1, midY1)
          ctx.quadraticCurveTo(curr.x, curr.y, midX2, midY2)
        }
        ctx.stroke()
        ctx.globalCompositeOperation = 'source-over'
        ctx.restore()
      }
    }
  }

  const handleEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (isPanning.current) {
      isPanning.current = false
      return
    }

    if (!isDrawing.current) return
    e.preventDefault()
    isDrawing.current = false

    const points = currentPoints.current
    if (points.length < 1) return

    // Push stroke to Yjs (syncs to all peers via y-websocket)
    const stroke: StrokeData = { tool, points, color, width: strokeWidth }
    isRemoteUpdate.current = true
    doc.getArray<StrokeData>('strokes').push([stroke])
    isRemoteUpdate.current = false

    // Full redraw to ensure consistency
    redraw()

    currentPoints.current = []
    shapeStart.current = null
    snapshotRef.current = null
  }

  // Wheel zoom (centered on mouse position)
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top

    const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * factor))

    // Adjust pan so zoom centers on mouse position
    const newPanX = mx - (mx - pan.x) * (newZoom / zoom)
    const newPanY = my - (my - pan.y) * (newZoom / zoom)

    setZoom(newZoom)
    setPan({ x: newPanX, y: newPanY })
  }, [zoom, pan])

  const handleClear = () => {
    const yStrokes = doc.getArray<StrokeData>('strokes')
    slog.action('whiteboard-clear', { roomId, strokes: yStrokes.length })
    isRemoteUpdate.current = true
    yStrokes.delete(0, yStrokes.length)
    isRemoteUpdate.current = false
    redraw()
  }

  const zoomIn = () => {
    const newZoom = Math.min(MAX_ZOOM, zoom * ZOOM_STEP)
    // Zoom toward center of container
    const container = containerRef.current
    if (container) {
      const rect = container.getBoundingClientRect()
      const cx = rect.width / 2
      const cy = rect.height / 2
      setPan({
        x: cx - (cx - pan.x) * (newZoom / zoom),
        y: cy - (cy - pan.y) * (newZoom / zoom),
      })
    }
    setZoom(newZoom)
  }

  const zoomOut = () => {
    const newZoom = Math.max(MIN_ZOOM, zoom / ZOOM_STEP)
    const container = containerRef.current
    if (container) {
      const rect = container.getBoundingClientRect()
      const cx = rect.width / 2
      const cy = rect.height / 2
      setPan({
        x: cx - (cx - pan.x) * (newZoom / zoom),
        y: cy - (cy - pan.y) * (newZoom / zoom),
      })
    }
    setZoom(newZoom)
  }

  const zoomPercent = Math.round(zoom * 100)

  // Prevent context menu on right-click (used for panning)
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
  }, [])

  return (
    <div className={embedded ? "w-full h-full bg-zinc-900 flex flex-col relative" : "absolute inset-0 bottom-12 bg-zinc-900 z-40 flex flex-col"}>
      {/* Floating toolbar */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-3 py-1.5 bg-zinc-900/90 backdrop-blur border border-zinc-700 rounded-lg shadow-lg">
        {/* Tools */}
        <button onClick={() => setTool('pencil')} className={`p-1.5 rounded ${tool === 'pencil' ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'}`} title="Crayon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M17 3a2.85 2.85 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>
        </button>
        <button onClick={() => setTool('eraser')} className={`p-1.5 rounded ${tool === 'eraser' ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'}`} title="Gomme">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M20 20H7L3 16l9-9 8 8-4 4z" /><path d="M6.5 13.5l5 5" /></svg>
        </button>
        <button onClick={() => setTool('rectangle')} className={`p-1.5 rounded ${tool === 'rectangle' ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'}`} title="Rectangle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
        </button>
        <button onClick={() => setTool('circle')} className={`p-1.5 rounded ${tool === 'circle' ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'}`} title="Cercle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><circle cx="12" cy="12" r="10" /></svg>
        </button>

        <div className="w-px h-5 bg-zinc-700" />

        {/* Widths */}
        {WIDTHS.map(w => (
          <button key={w.value} onClick={() => setStrokeWidth(w.value)} className={`px-1.5 py-0.5 text-xs rounded ${strokeWidth === w.value ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'}`} title={`${w.value}px`}>
            {w.label}
          </button>
        ))}

        <div className="w-px h-5 bg-zinc-700" />

        {/* Colors */}
        {COLORS.map(c => (
          <button key={c} onClick={() => setColor(c)} className={`w-5 h-5 rounded-full border-2 ${color === c ? 'border-blue-400 ring-1 ring-blue-400' : 'border-zinc-600'} ${c === '#ffffff' ? 'bg-white' : ''}`} style={{ backgroundColor: c }} title={c} />
        ))}

        <div className="w-px h-5 bg-zinc-700" />

        {/* Zoom */}
        <button onClick={zoomOut} className="p-1 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded" title="Zoom -">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35M8 11h6" /></svg>
        </button>
        <span className="text-xs text-zinc-400 min-w-[36px] text-center">{zoomPercent}%</span>
        <button onClick={zoomIn} className="p-1 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded" title="Zoom +">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35M11 8v6M8 11h6" /></svg>
        </button>
        <button onClick={fitToView} className="p-1 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded" title="Ajuster a l'ecran">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
        </button>

        <div className="w-px h-5 bg-zinc-700" />

        {/* Clear + Sync + Close */}
        <button onClick={handleClear} className="p-1 text-red-400 hover:text-red-300 hover:bg-zinc-800 rounded" title="Effacer tout">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" /></svg>
        </button>
        <span className={`w-2 h-2 rounded-full ${providerRef.current?.wsconnected ? 'bg-green-400' : 'bg-red-400'}`} title="Sync" />
        <button onClick={onClose} className="p-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded" title="Fermer">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      {/* Canvas area — full surface */}
      <div ref={containerRef} className="absolute inset-0 overflow-hidden bg-zinc-800 cursor-crosshair">
        <canvas
          ref={canvasRef}
          className="block"
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          onWheel={handleWheel}
          onContextMenu={handleContextMenu}
        />
      </div>
    </div>
  )
}

// Draw a stroke on a 2D context (already transformed to virtual coords)
function drawStrokeOnCtx(ctx: CanvasRenderingContext2D, s: StrokeData, _zoom: number) {
  const { points, color, width, tool } = s

  if (tool === 'eraser') {
    ctx.globalCompositeOperation = 'destination-out'
  } else {
    ctx.globalCompositeOperation = 'source-over'
  }

  ctx.strokeStyle = color
  ctx.lineWidth = width
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  if (tool === 'rectangle' && points.length >= 2) {
    const start = points[0], end = points[points.length - 1]
    ctx.beginPath()
    ctx.rect(start.x, start.y, end.x - start.x, end.y - start.y)
    ctx.stroke()
    ctx.globalCompositeOperation = 'source-over'
    return
  }

  if (tool === 'circle' && points.length >= 2) {
    const start = points[0], end = points[points.length - 1]
    const cx = (start.x + end.x) / 2, cy = (start.y + end.y) / 2
    const rx = Math.abs(end.x - start.x) / 2, ry = Math.abs(end.y - start.y) / 2
    ctx.beginPath()
    ctx.ellipse(cx, cy, Math.max(rx, 0.1), Math.max(ry, 0.1), 0, 0, Math.PI * 2)
    ctx.stroke()
    ctx.globalCompositeOperation = 'source-over'
    return
  }

  if (points.length < 2) { ctx.globalCompositeOperation = 'source-over'; return }

  ctx.beginPath()
  ctx.moveTo(points[0].x, points[0].y)
  if (points.length === 2) {
    ctx.lineTo(points[1].x, points[1].y)
  } else {
    for (let i = 1; i < points.length - 1; i++) {
      const midX = (points[i].x + points[i + 1].x) / 2
      const midY = (points[i].y + points[i + 1].y) / 2
      ctx.quadraticCurveTo(points[i].x, points[i].y, midX, midY)
    }
    ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y)
  }
  ctx.stroke()
  ctx.globalCompositeOperation = 'source-over'
}
